import { useRouter } from "next/router";
import { memo, useCallback, useMemo } from "react";
import { atom, useRecoilState } from "recoil";
import { Button } from "src/components/elements/button/Button";
import { AheadCaretIcon } from "src/components/elements/icon/AheadCaretIcon";
import { Text } from "src/components/elements/typography/Text";
import { useGetUserData } from "src/modules/api/User/getUserData/useGetUserData";
import { useUpdateSelectCreditCardTrigger } from "src/modules/api/User/updateSelectCreditCard/useUpdateSelectCreditCardTrigger";
import { CreditCard } from "src/modules/domain/order/payment/creditCard";
import { PersonalCreditCardData } from "src/modules/domain/order/payment/creditCard/personalCreditCardData";
import {
  PaymentType,
  PAYMENT_TYPE,
} from "src/modules/domain/order/payment/paymentType/paymentType";
import { AuPayButton } from "./AuPayButton";
import { CreditCardButton } from "./CreditCardButton";
import styles from "./styles.module.scss";

type Props = {
  updateCache: (
    data?: PersonalCreditCardData,
    shouldRevalidate?: boolean
  ) => void;
  list?: CreditCard[];
  selectedIndex?: number;
};

export const selectedPaymentMethodState = atom<PaymentType>({
  key: "selectedPaymentMethodState",
  default: PAYMENT_TYPE.CREDIT,
});

export const PaymentSelection = memo(
  ({ list, selectedIndex, updateCache }: Props) => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useRecoilState(
      selectedPaymentMethodState
    );
    const router = useRouter();
    const { trigger: updateSelectCreditCardTrigger } =
      useUpdateSelectCreditCardTrigger();
    const handleSelectCreditCard = useCallback(
      async (id: CreditCard["card_id"]) => {
        if (!list || typeof selectedIndex === "undefined") {
          return;
        }
        const updatedCardData = await updateSelectCreditCardTrigger({
          params: {
            card_id: id,
          },
        });
        updateCache(updatedCardData, false);
      },
      [list, selectedIndex, updateSelectCreditCardTrigger, updateCache]
    );
    const handleAddCreditCardClick = useCallback(
      () => router.push("/mypage/payment/register"),
      [router]
    );
    const handleAUSelect = useCallback(() => {
      setSelectedPaymentMethod(PAYMENT_TYPE.AU);
    }, [setSelectedPaymentMethod]);
    const isButtonAUSelected = useMemo(
      () => selectedPaymentMethod === PAYMENT_TYPE.AU,
      [selectedPaymentMethod]
    );

    const { data: userData } = useGetUserData();
    const isSalesUser = userData?.is_sales_user;

    return (
      <>
        <Text size="size-30" weight="500" className={styles.header}>
          支払い方法
        </Text>
        <div className={styles.cardButtonList}>
          {list && list.length !== 0 && typeof selectedIndex !== "undefined" ? (
            <div className={styles.cardButtonContainer}>
              <CreditCardButton
                type={list[selectedIndex].card_company}
                number={list[selectedIndex].card_number}
                id={list[selectedIndex].card_id}
                onSelectCard={handleSelectCreditCard}
              />
            </div>
          ) : (
            <Button
              buttonType="buttonColorFrameless"
              px="space-0"
              buttonContainerClassName={styles.addCreditCardButton}
              onClick={handleAddCreditCardClick}
            >
              <div className={styles.addCreditCardButtonInner}>
                <Text size="size-16" className={styles.text}>
                  新しいカードを追加する
                </Text>
                <AheadCaretIcon />
              </div>
            </Button>
          )}
          {/* Temporarily hiding the AU Pay for non sales users MENUENG - 35787 */}
          {isSalesUser && (
            <div>
              <AuPayButton
                isSelected={isButtonAUSelected}
                onSelect={handleAUSelect}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);
